import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Stack,
  InputAdornment,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Position } from '../types';
import { PositionService } from '../services/positionService';
import { formatCurrency, formatPercent } from '../utils/formatters';
import { format } from 'date-fns';

interface ClosePositionDialogProps {
  open: boolean;
  onClose: () => void;
  position: Position | null;
  onPositionClosed: () => void;
}

const ClosePositionDialog: React.FC<ClosePositionDialogProps> = ({
  open,
  onClose,
  position,
  onPositionClosed,
}) => {
  const [exitPrice, setExitPrice] = useState('');
  const [exitDate, setExitDate] = useState(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (position) {
      setExitPrice(position.currentPrice.toFixed(2));
      setExitDate(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    }
  }, [position]);

  const handleClose = () => {
    setExitPrice('');
    setExitDate(format(new Date(), "yyyy-MM-dd'T'HH:mm"));
    setError(null);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleClosePosition = async () => {
    if (!position) return;

    setError(null);
    setLoading(true);

    try {
      const price = parseFloat(exitPrice);
      const exitDateObj = new Date(exitDate);

      if (isNaN(price) || price <= 0) {
        throw new Error('Please enter a valid exit price');
      }

      await PositionService.closePosition(
        position.id,
        price,
        exitDateObj
      );

      onPositionClosed();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to close position');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePosition = async () => {
    if (!position) return;

    setError(null);
    setLoading(true);

    try {
      await PositionService.deletePosition(position.id);
      onPositionClosed();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete position');
    } finally {
      setLoading(false);
    }
  };

  if (!position) return null;

  const exitPriceNum = parseFloat(exitPrice) || 0;
  const totalProceeds = exitPriceNum * position.quantity;
  const totalCost = position.entryPrice * position.quantity;
  const gainLoss = totalProceeds - totalCost;
  const gainLossPercent = totalCost > 0 ? (gainLoss / totalCost) * 100 : 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            Close Position - {position.symbol}
            <Typography variant="body2" color="textSecondary">
              {position.quantity} shares @ {formatCurrency(position.entryPrice)}
            </Typography>
          </Box>
          <Tooltip title="Delete position (removes all records)">
            <IconButton
              onClick={() => setShowDeleteConfirm(true)}
              color="error"
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Exit Price"
            type="number"
            value={exitPrice}
            onChange={(e) => setExitPrice(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: { step: 0.01, min: 0 }
            }}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Exit Date/Time"
            type="datetime-local"
            value={exitDate}
            onChange={(e) => setExitDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 2 }} />

          <Typography variant="h6" gutterBottom>
            Trade Summary
          </Typography>
          
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="textSecondary">
                Entry Cost:
              </Typography>
              <Typography variant="body2">
                {formatCurrency(totalCost)}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="textSecondary">
                Exit Proceeds:
              </Typography>
              <Typography variant="body2">
                {formatCurrency(totalProceeds)}
              </Typography>
            </Box>
            
            <Divider />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight="medium">
                Gain/Loss:
              </Typography>
              <Typography 
                variant="body1" 
                fontWeight="medium"
                color={gainLoss >= 0 ? 'success.main' : 'error.main'}
              >
                {gainLoss >= 0 ? '+' : ''}
                {formatCurrency(gainLoss)} ({formatPercent(gainLossPercent)})
              </Typography>
            </Box>
            
            <Alert 
              severity="info" 
              sx={{ mt: 2 }}
              icon={false}
            >
              <Typography variant="body2">
                <strong>Fund Impact:</strong><br />
                • Available Funds will increase by {formatCurrency(totalProceeds)}<br />
                • Portfolio earnings will {gainLoss >= 0 ? 'increase' : 'decrease'} by {formatCurrency(Math.abs(gainLoss))}
              </Typography>
            </Alert>
          </Stack>

          {showDeleteConfirm && (
            <Alert 
              severity="warning" 
              sx={{ mt: 3 }}
              action={
                <Stack direction="row" spacing={1}>
                  <Button 
                    size="small" 
                    color="inherit"
                    onClick={() => setShowDeleteConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    size="small" 
                    color="error"
                    variant="contained"
                    onClick={handleDeletePosition}
                    disabled={loading}
                  >
                    Delete
                  </Button>
                </Stack>
              }
            >
              <Typography variant="body2" fontWeight="medium">
                Delete this position?
              </Typography>
              <Typography variant="body2">
                This will permanently remove the position and all related transaction history. 
                The portfolio balance will not be affected. This action cannot be undone.
              </Typography>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleClosePosition}
          disabled={loading || !exitPrice || exitPriceNum <= 0 || showDeleteConfirm}
          color={gainLoss >= 0 ? 'success' : 'error'}
        >
          Close Position
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClosePositionDialog;